import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface PresenceUser {
  userId: string;
  name: string;
  department: string;
  departmentLabel: string;
  role: string;
  online: boolean;
  section: string;
  lastSeenAt: string | null;
}

export interface PresenceResponse {
  onlineCount: number;
  totalUsers: number;
  users: PresenceUser[];
  bySection: { section: string; members: { userId: string; name: string; departmentLabel: string }[] }[];
}

export interface OversightConversation {
  id: string;
  type: 'dm' | 'group' | 'department' | 'company';
  label: string;
  department: string;
  memberCount: number | null;
  lastMessageAt: string | null;
  lastMessagePreview: string;
  lastMessageByName: string;
}

export interface OversightMessage {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  createdAt: string;
}

export interface CommLogEntry {
  id: string;
  channel: 'email' | 'whatsapp';
  direction: 'out' | 'in';
  to: string;
  from: string;
  subject: string;
  body: string;
  status: string;
  createdAt: string;
}

export function usePresence() {
  return useQuery({
    queryKey: ['oversight', 'presence'],
    queryFn: async () => (await api.get<PresenceResponse>('/api/oversight/presence')).data,
    refetchInterval: 10_000,
  });
}

export function useOversightConversations() {
  return useQuery({
    queryKey: ['oversight', 'conversations'],
    queryFn: async () => (await api.get<{ conversations: OversightConversation[] }>('/api/oversight/conversations')).data.conversations,
    refetchInterval: 15_000,
  });
}

export function useOversightMessages(conversationId: string | null) {
  return useQuery({
    queryKey: ['oversight', 'messages', conversationId],
    queryFn: async () =>
      (await api.get<{ conversation: { id: string; type: string; name: string }; messages: OversightMessage[] }>(
        `/api/oversight/conversations/${conversationId}/messages`,
      )).data,
    enabled: !!conversationId,
  });
}

export function useCommLog(channel: '' | 'email' | 'whatsapp') {
  return useQuery({
    queryKey: ['oversight', 'comm-log', channel],
    queryFn: async () => {
      const suffix = channel ? `?channel=${channel}` : '';
      return (await api.get<{ logs: CommLogEntry[] }>(`/api/oversight/comm-log${suffix}`)).data.logs;
    },
  });
}

export interface ActivityEntry {
  id?: string;
  _id?: string;
  user_name?: string;
  display_name?: string;
  role?: string;
  action?: string;
  entity_type?: string;
  detail?: string;
  created_at?: string;
}

export function useAllActivity(userId: string) {
  return useQuery({
    queryKey: ['oversight', 'activity', userId],
    queryFn: async () => {
      const suffix = userId ? `?userId=${userId}&limit=150` : '?limit=150';
      return (await api.get<{ activity: ActivityEntry[] }>(`/api/activity${suffix}`)).data.activity;
    },
    refetchInterval: 15_000,
  });
}
